import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, TrendingUp, Download, DollarSign, CheckCircle2, Clock, X, ChevronRight } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorWalletScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    balance,
    pendingPayout,
    totalEarnings,
    orders
  } = useVendor();
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Mock transaction history
  const transactions = [{
    id: 'T001',
    type: 'credit' as const,
    amount: 800000,
    description: 'Order Payment - VO-001',
    date: new Date().toISOString(),
    status: 'completed' as const
  }, {
    id: 'T002',
    type: 'debit' as const,
    amount: 500000,
    description: 'Withdrawal to Bank',
    date: new Date(Date.now() - 86400000).toISOString(),
    status: 'completed' as const
  }, {
    id: 'T003',
    type: 'credit' as const,
    amount: 750000,
    description: 'Order Payment - VO-002',
    date: new Date(Date.now() - 172800000).toISOString(),
    status: 'pending' as const
  }];
  const handleWithdraw = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowWithdrawModal(false);
      setShowSuccess(true);
      setWithdrawAmount('');
      setTimeout(() => setShowSuccess(false), 3000);
    }, 1500);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-2579">
      <Header title="Wallet & Earnings" data-id="element-2580" />

      <div className="p-4 space-y-4" data-id="element-2581">
        {/* Balance Card */}
        <div className="bg-gradient-to-br from-primary to-primary-600 p-6 rounded-2xl text-white shadow-lg" data-id="element-2582">
          <div className="flex items-center gap-2 mb-2" data-id="element-2583">
            <Wallet className="w-5 h-5" data-id="element-2584" />
            <span className="text-sm opacity-90" data-id="element-2585">Available Balance</span>
          </div>
          <h2 className="text-4xl font-bold mb-4" data-id="element-2586">
            ₦{balance.toLocaleString()}
          </h2>
          <Button fullWidth variant="outline" onClick={() => navigate(Screen.VENDOR_WITHDRAWAL)} className="bg-white/20 border-white/30 text-white hover:bg-white/30" icon={<Download className="w-4 h-4" data-id="element-2588" />} data-id="element-2587">
            Withdraw Funds
          </Button>
        </div>

        {/* Earnings Summary */}
        <div className="grid grid-cols-2 gap-3" data-id="element-2589">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-2590">
            <div className="flex items-center gap-2 mb-2" data-id="element-2591">
              <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center" data-id="element-2592">
                <Clock className="w-4 h-4 text-warning" data-id="element-2593" />
              </div>
              <span className="text-xs text-gray-500" data-id="element-2594">Pending Payout</span>
            </div>
            <p className="text-xl font-bold text-gray-900" data-id="element-2595">
              ₦{(pendingPayout / 1000).toFixed(0)}k
            </p>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-2596">
            <div className="flex items-center gap-2 mb-2" data-id="element-2597">
              <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center" data-id="element-2598">
                <TrendingUp className="w-4 h-4 text-success" data-id="element-2599" />
              </div>
              <span className="text-xs text-gray-500" data-id="element-2600">Total Earnings</span>
            </div>
            <p className="text-xl font-bold text-gray-900" data-id="element-2601">
              ₦{(totalEarnings / 1000).toFixed(0)}k
            </p>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-2602">
          <div className="flex items-center justify-between mb-3" data-id="element-2603">
            <h3 className="font-bold text-gray-900" data-id="element-2604">Transaction History</h3>
            <button onClick={() => navigate(Screen.VENDOR_TRANSACTIONS)} className="text-sm font-medium text-primary flex items-center gap-1" data-id="element-2605">
              View All <ChevronRight className="w-4 h-4" data-id="element-2606" />
            </button>
          </div>
          <div className="space-y-3" data-id="element-2607">
            {transactions.map(txn => <div key={txn.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-id="element-2608">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${txn.type === 'credit' ? 'bg-success/10' : 'bg-error/10'}`} data-id="element-2609">
                  {txn.type === 'credit' ? <TrendingUp className="w-5 h-5 text-success" data-id="element-2610" /> : <Download className="w-5 h-5 text-error" data-id="element-2611" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-2612">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2613">
                    {txn.description}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2614">
                    {new Date(txn.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                  </p>
                </div>
                <div className="text-right" data-id="element-2615">
                  <p className={`text-sm font-bold ${txn.type === 'credit' ? 'text-success' : 'text-error'}`} data-id="element-2616">
                    {txn.type === 'credit' ? '+' : '-'}₦
                    {(txn.amount / 1000).toFixed(0)}k
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${txn.status === 'completed' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`} data-id="element-2617">
                    {txn.status}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      <AnimatePresence data-id="element-2618">
        {showWithdrawModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowWithdrawModal(false)} className="fixed inset-0 bg-black/50 z-50" data-id="element-2619" />
            <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 50
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 z-50 max-w-md mx-auto" data-id="element-2620">
              <div className="flex items-center justify-between mb-4" data-id="element-2621">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-2622">
                  Withdraw Funds
                </h3>
                <button onClick={() => setShowWithdrawModal(false)} className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-2623">
                  <X className="w-5 h-5 text-gray-600" data-id="element-2624" />
                </button>
              </div>

              <div className="mb-4" data-id="element-2625">
                <p className="text-sm text-gray-600 mb-2" data-id="element-2626">Available Balance</p>
                <p className="text-2xl font-bold text-primary" data-id="element-2627">
                  ₦{balance.toLocaleString()}
                </p>
              </div>

              <div className="mb-6" data-id="element-2628">
                <label className="text-sm font-medium text-gray-700 block mb-2" data-id="element-2629">
                  Withdrawal Amount
                </label>
                <div className="relative" data-id="element-2630">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" data-id="element-2631">
                    ₦
                  </span>
                  <input type="number" placeholder="0" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-id="element-2632" />
                </div>
                <p className="text-xs text-gray-500 mt-2" data-id="element-2633">
                  Funds will be transferred to your registered bank account
                </p>
              </div>

              <Button fullWidth size="lg" onClick={handleWithdraw} loading={loading} disabled={!withdrawAmount || parseFloat(withdrawAmount) > balance} data-id="element-2634">
                Confirm Withdrawal
              </Button>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Success Toast */}
      <AnimatePresence data-id="element-2635">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-4 right-4 z-50 flex justify-center" data-id="element-2636">
            <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2" data-id="element-2637">
              <CheckCircle2 className="w-5 h-5 text-success" data-id="element-2638" />
              <span className="text-sm font-medium" data-id="element-2639">
                Withdrawal request submitted
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}